import { useEffect, useRef } from 'react';

import classNames from '@/helpers/classNames';

import { SingleSelectOptionProps } from '../../BaseSelect/types';
import styles from './SelectOption.module.scss';

export default function SingleSelectOption<T>(
  props: SingleSelectOptionProps<T>
) {
  const {
    name,
    option,
    onKeyDown,
    selected,
    onClick,
    customOption,
    truncated,
    shouldFocus,
    shouldScrollTo,
    className,
  } = props;
  const ref = useRef<HTMLLIElement>(null);
  const { isDisabled } = option;

  useEffect(() => {
    if (shouldFocus) {
      ref?.current?.focus();
    }
  }, [shouldFocus]);

  useEffect(() => {
    if (shouldScrollTo) {
      const optionElement = ref?.current;
      const listElement = optionElement?.parentElement;

      if (optionElement && listElement) {
        listElement.scroll({
          top: optionElement.offsetTop - optionElement.offsetHeight,
        });
      }
    }
  }, [shouldScrollTo]);

  return (
    <li
      ref={ref}
      id={`${name}_element_${String(option.value)}`}
      className={classNames([styles.option, className], {
        [styles.truncated]: truncated,
        [styles.disabled]: isDisabled,
        [styles.selected]: option.value === selected,
      })}
      role="option"
      aria-selected={option.value === selected && !isDisabled}
      tabIndex={isDisabled ? undefined : 0}
      onKeyDown={isDisabled ? undefined : onKeyDown(option.value, true)}
      onClick={isDisabled ? undefined : () => onClick(option.value, true)}
    >
      {customOption ? (
        customOption({ option, selected })
      ) : (
        <>
          {truncated ? (
            <abbr className={styles.abbr} title={option.label}>
              {option.label}
            </abbr>
          ) : (
            option.label
          )}
        </>
      )}
    </li>
  );
}
